/* eslint-disable max-lines-per-function */
import _ from "lodash";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  InputTypeAhead,
  PrimaryButton,
  SecondaryButton,
  ButtonSizeOptions,
  LabelDollarFormatted,
  LabelDecimal
} from "@loanbossinc/component-library";
import moment from "moment";
import numeral from "numeral";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import clsx from "clsx";

import {
  ResultPreviewLabel,
  ResultPreview,
  StyledLockedSwitch,
  AnnualizedText,
  SwitchWrapper,
  IncludeLeaseSelect,
  CellBackgroundWrapper,
  DataTableCell,
  InfoTableCell,
  DataTableRow,
  InfoTableRow,
  VacancyHeaderRow,
  VacancyTableLeftWrapper,
  VacancyTableRightWrapper,
  VacancyTableWrapper,
  Screen2Wrapper,
  InfoCircle,
  InfoText,
  StyledToolTip,
  StyledButtonText
} from "./rentRollFieldStyles";
import InfoIcon from "./Group.svg";
import StyledTooltip from "./rentRollTooltip";

const LB_BLUE = "#005AA9";

const VacancyRateCalculation = forwardRef((props, ref) => {
  const {
    deal,
    fields,
    actualVacancyType,
    existingOverrides,
    pendingOverrides,
    getRateCalculatedData,
    getRentRollOverrides,
    isAdding,
    isAnnualized,
    isCalculatedDataLoading,
    marketRentType,
    putRentRollField,
    rateCalculationData,
    reportConfigs,
    selectedProperty,
    selectedRentRollField,
    selectedReportGroup,
    setFieldSelections,
    setRentRollFieldSelections,
    isAnythingWasUpdated,
    isSavingField,
    isEditingLineItem,
    setNoiCalculationsDashboardSelections,
    setDrawerSelections,
    setNewLineItemsSelectionsToDefault
  } = props;
  let drawerMaxWidth = "1075px";

  const [calculationState, setCalculationState] = useState({
    selectedRGId: null,
    selectedAsOfDate: null,
    reportGroupsOptions: null,
    asOfDateOptions: null
  });

  const [areAllLeasesVacant, setAreAllLeasesVacant] = useState(null);

  const MARKET_RENT = "Market Rent";

  const getIncludeLeaseOptions = lease => {
    if (lease.isVacant) {
      return [
        { name: "Exclude Suite", value: "exclude" },
        { name: MARKET_RENT, value: "marketRent" }
      ];
    }

    return [
      { name: "Included Lease", value: "include" },
      { name: "Excluded Lease", value: "exclude" },
      { name: MARKET_RENT, value: "marketRent" }
    ];
  };

  useEffect(() => {
    if (reportConfigs && !calculationState.reportGroupsOptions && !selectedReportGroup) {
      setCalculationState(prev => ({
        ...prev,
        reportGroupsOptions: reportConfigs.map(rg => ({ name: rg.reportName, id: rg.id }))
      }));
    }
  }, [reportConfigs, selectedReportGroup]);

  const onAsOfDateSelected = value => {
    setCalculationState(prev => ({ ...prev, selectedAsOfDate: value }));
    setAreAllLeasesVacant(null);
  };

  useEffect(() => {
    if (deal.id && selectedRentRollField && calculationState.selectedRGId && calculationState.selectedAsOfDate) {
      const data = {
        dealId: deal.id,
        fieldId: selectedRentRollField.id,
        reportGroupId: calculationState.selectedRGId,
        propertyId: selectedRentRollField.propertyId,
        asOfDate: calculationState.selectedAsOfDate
      };
      getRentRollOverrides({ data });
    }
  }, [calculationState.selectedAsOfDate, deal, selectedRentRollField]);

  // set pending override based on existing override
  useEffect(() => {
    if (existingOverrides) {
      setRentRollFieldSelections({
        pendingOverrides: _.cloneDeep(existingOverrides)
      });
    }
  }, [existingOverrides]);

  // get calculated data after pending override is set
  useEffect(() => {
    if (pendingOverrides) {
      const data = {
        dealId: deal.id,
        fieldId: selectedRentRollField.id,
        reportGroupId: calculationState.selectedRGId,
        propertyId: selectedRentRollField.propertyId,
        asOfDate: calculationState.selectedAsOfDate,
        overrides: pendingOverrides,
        saveResults: false
      };
      getRateCalculatedData({ data });
    }
  }, [pendingOverrides]);

  useEffect(() => {
    if (selectedReportGroup || reportConfigs.length === 1) {
      const datesArray = selectedReportGroup ? selectedReportGroup.dates : reportConfigs[0].dates;
      if (datesArray) {
        const filteredDates = datesArray.filter(date => moment() < moment(date.asOfDate));
        let asOfDate;

        if (selectedRentRollField && selectedRentRollField.asOfDate) {
          asOfDate = selectedRentRollField.asOfDate;
        } else {
          if (filteredDates && !filteredDates.length) {
            asOfDate = datesArray[datesArray.length - 1]?.asOfDate;
          } else {
            asOfDate = filteredDates[0]?.asOfDate || null;
          }
        }

        setCalculationState(prev => ({
          ...prev,
          reportGroupsOptions: reportConfigs.map(rg => ({ name: rg.reportName, id: rg.id })),
          asOfDateOptions: datesArray.map(date => ({ formattedDate: moment.utc(date.asOfDate).format("MMM DD, YYYY"), asOfDate: date.asOfDate })),
          selectedRGId: selectedReportGroup ? selectedReportGroup.id : reportConfigs[0].id
        }));

        onAsOfDateSelected(asOfDate);
      }
    }
  }, [selectedReportGroup, reportConfigs, selectedRentRollField]);

  useEffect(() => {
    if (selectedRentRollField) {
      setRentRollFieldSelections({ isAnnualized: selectedRentRollField.isRentRollAnnualized });
    }
  }, [selectedRentRollField]);

  useEffect(() => {
    if (rateCalculationData && rateCalculationData.suites) {
      let isOccupied = false;
      for (const suite of rateCalculationData.suites) {
        const occupied = suite.leases.find(l => l.lessee !== "Vacant");
        if (occupied) {
          setAreAllLeasesVacant(false);
          isOccupied = true;
          break;
        }
      }
      if (!isOccupied) {
        setAreAllLeasesVacant(true);
      }
    }
  }, [rateCalculationData, setAreAllLeasesVacant]);

  const onRGSelected = value => {
    if (!value) {
      setCalculationState(prev => ({ ...prev, selectedAsOfDate: null, selectedRGId: null }));
    } else {
      const asOfDatesList = reportConfigs.find(rc => rc.id === value).dates || [];
      const filteredDates = asOfDatesList.filter(date => moment() < moment(date.asOfDate));
      let asOfDate;
      if (filteredDates && !filteredDates.length) {
        asOfDate = asOfDatesList[asOfDatesList.length - 1]?.asOfDate;
      } else {
        asOfDate = filteredDates[0]?.asOfDate || null;
      }
      const asOfDateOptions = asOfDatesList.map(date => ({ formattedDate: moment.utc(date.asOfDate).format("MMM DD, YYYY"), asOfDate: date.asOfDate }));
      setCalculationState(prev => ({ ...prev, selectedRGId: value, asOfDateOptions }));
      onAsOfDateSelected(asOfDate);
    }
  };

  const onBackClick = () => {
    if (isAnythingWasUpdated) {
      setRentRollFieldSelections({ showConfirmationModal: true, locationModalIsOpenFrom: "screen2" });
    } else {
      setRentRollFieldSelections({ isStep2Screen: false });
    }
  };

  const onAnnualizeSwitch = () => {
    setRentRollFieldSelections({ isAnnualized: !isAnnualized, isAnythingWasUpdated: true });
  };

  const handleOverride = (action, suiteId, leaseId) => {
    let updatedPendingOverrides;
    const existingPendingOverride = pendingOverrides && pendingOverrides.find(override => override.leaseId === leaseId && override.suiteId === suiteId);

    if (existingPendingOverride) {
      updatedPendingOverrides = pendingOverrides.map((override) => {
        if (override.leaseId === leaseId && override.suiteId === suiteId) {
          return { ...override, isIncluded: action === "include", isMarketRentOnly: action === "marketRent" };
        }
        return override;
      });
    } else {
      updatedPendingOverrides = [
        ...pendingOverrides,
        {
          dealId: deal.id,
          propertyId: selectedProperty.id,
          fieldId: selectedRentRollField.id,
          reportGroupId: calculationState.selectedRGId,
          asOfDate: calculationState.selectedAsOfDate,
          leaseId,
          suiteId,
          isIncluded: action === "include",
          isMarketRentOnly: action === "marketRent"
        }
      ];
    }

    setRentRollFieldSelections({ pendingOverrides: updatedPendingOverrides, isAnythingWasUpdated: true });
  };

  const onFinishClick = () => {
    const calculatorData = {
      dealId: deal.id,
      fieldId: selectedRentRollField.id,
      reportGroupId: calculationState.selectedRGId,
      propertyId: selectedRentRollField.propertyId,
      asOfDate: calculationState.selectedAsOfDate,
      overrides: pendingOverrides,
      saveResults: true
    };
    getRateCalculatedData({ data: calculatorData });

    const fieldData = {
      ...selectedRentRollField,
      isRentRollAnnualized: isAnnualized,
      value: isAnnualized ? rateCalculationData.totalAnnualized : rateCalculationData.total,
      asOfDate: calculationState.selectedAsOfDate
    };

    putRentRollField({ data: fieldData });

    const updatedFieldState = {
      currentSelectedFieldType: null,
      isEditing: false,
      isAdding: false
    };

    if (selectedRentRollField) {
      let updatedFieldsList;
      if (isAdding) {
        updatedFieldsList = fields ? [...fields, fieldData] : [fieldData];
      } else if (!fields || !fields.length) {
        updatedFieldsList = [fieldData];
      } else {
        const filtered = fields.filter(f => f.id !== fieldData.id);
        updatedFieldsList = [...filtered, fieldData];
      }
      updatedFieldState.fields = updatedFieldsList;
      setFieldSelections(updatedFieldState);
    }

    if (isEditingLineItem) {
      setNoiCalculationsDashboardSelections({
        initialDrawerSectionName: null,
        isLenderReportingDrawerOpen: false,
        addLineItemType: null,
        lineItemToEdit: null
      });
      setDrawerSelections({ isEditingLineItem: false, currentSectionName: null });
      setNewLineItemsSelectionsToDefault();
    }
  };

  useImperativeHandle(ref, () => ({
    onFinishClick
  }));

  const getTooltipContent = period => (
    <div style={{ display: "flex", flexDirection: "column", padding: "0.5em" }}>
      <StyledToolTip>
        Base Rent:
        {" "}
        {numeral(period.rentValue).format("$0,00.00") || "--"}
      </StyledToolTip>
      <StyledToolTip>
        CAM:
        {" "}
        {numeral(period.camValue).format("$0,00.00") || "--"}
      </StyledToolTip>
      <p style={{ margin: "0", fontFamily: "Work Sans", fontSize: "12px", fontWeight: "normal" }}>
        Misc:
        {" "}
        {numeral(period.miscellaneousValue).format("$0,00.00") || "--"}
      </p>
    </div>
  );

  const getStraightLineTooltipContent = period => (
    <div style={{ display: "flex", flexDirection: "column", padding: "0.5em" }}>
      <StyledToolTip>
        Lease Rent Average:
        {" "}
        {numeral(period.leaseAverageRentValue).format("$0,00.00") || "--"}
      </StyledToolTip>
      <StyledToolTip>
        Base Rent:
        {" "}
        {numeral(period.rentValue).format("$0,00.00") || "--"}
      </StyledToolTip>
    </div>
  );

  const getOverrideButtonText = (lease) => {
    let content;
    if (lease.isMarketRentOnly) {
      content = lease.isOverridden ? "Manual Market Rent" : MARKET_RENT;
    } else if (lease.isIncluded) {
      content = lease.isOverridden ? "Manual Include" : "Included Lease";
    } else {
      if (lease.isOverridden) {
        content = "Manual Exclude";
      } else {
        content = lease.isVacant ? "Exclude Suite" : "Excluded Lease";
      }
    }

    return <div>{content}</div>;
  };

  const getTable = () => {
    if (isCalculatedDataLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "2em" }}>
          <CircularProgress size={40} style={{ color: "#005AA9" }} />
        </div>
      );
    }

    if (!rateCalculationData || !existingOverrides) {
      return null;
    }

    return (
      <VacancyTableWrapper>
        <VacancyTableLeftWrapper>
          <InfoTableRow header border="1px solid #DADCE0">
            <InfoTableCell align="left" w="10%" p="0.2em 0.2em 0 1em" bold>
              SUITE
            </InfoTableCell>
            <InfoTableCell align="center" w="15%" bold>
              SQ.FT.
            </InfoTableCell>
            <InfoTableCell align="center" w="35%" bold>
              CALCULATION STATUS
            </InfoTableCell>
            <InfoTableCell align="left" w="40%" p="0 0 0 0.3em" bold>
              LESSEE
            </InfoTableCell>
          </InfoTableRow>
          {rateCalculationData.suites &&
            _.orderBy(rateCalculationData.suites, "name", "asc").map(suite => {
              const { name, squareFootage, leases } = suite;

              return leases.map((lease, leaseIdx) => {
                let leaseSelectValue;
                if (lease.isMarketRentOnly) {
                  leaseSelectValue = "marketRent";
                } else if (lease.isIncluded) {
                  leaseSelectValue = "include";
                } else {
                  leaseSelectValue = "exclude";
                }

                return (
                  <InfoTableRow key={lease.id} border={leaseIdx < leases.length - 1 && "1px dashed #DADCE0"}>
                    <InfoTableCell align="left" w="10%" p="0.2em 0.2em 0 1em" style={{ color: LB_BLUE }}>
                      {leaseIdx === 0 && name}
                    </InfoTableCell>
                    <InfoTableCell align="center" w="15%" style={{ fontFamily: "Roboto Mono" }}>
                      {leaseIdx === 0 && numeral(squareFootage).format("0,0")}
                    </InfoTableCell>

                    <InfoTableCell align="center" w="35%">

                      <IncludeLeaseSelect
                        className={clsx(lease.isIncluded || lease.isMarketRentOnly ? "included" : null)}
                        isOverridden={lease.isOverridden}
                        isMarketRentOnly={lease.isMarketRentOnly}
                        disableUnderline
                        id="isIncluded"
                        name="isIncluded"
                        onChange={event => handleOverride(event.target.value, suite.suiteId, lease.leaseId)}
                        value={leaseSelectValue}
                        renderValue={() => getOverrideButtonText(lease)}
                      >
                        {getIncludeLeaseOptions(lease).map(option => (
                          <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>
                        ))}
                      </IncludeLeaseSelect>
                    </InfoTableCell>
                    <InfoTableCell align="left" w="40%" p="0 0 0 0.3em">
                      {lease.isUsingFirstRealChargeForVacancy ? "* " : ""}
                      {lease.lessee}
                    </InfoTableCell>
                  </InfoTableRow>
                );
              });
            })}
          <InfoTableRow>
            <CellBackgroundWrapper w="26%" justify="center">
              <InfoTableCell bold>
                MARKET RENT
              </InfoTableCell>
            </CellBackgroundWrapper>
            <CellBackgroundWrapper w="35%" justify="center" bg="none">
              <InfoTableCell style={{ fontSize: "14px", fontFamily: "Space Mono" }}>
                {selectedRentRollField.isStraightLine
                  ? "--"
                  : `${numeral(rateCalculationData.marketRent).format("$0,00.00")}/ft/month`}
              </InfoTableCell>
            </CellBackgroundWrapper>
            <CellBackgroundWrapper w="35%" justify="center">
              <InfoTableCell bold>TOTAL</InfoTableCell>
            </CellBackgroundWrapper>
          </InfoTableRow>
        </VacancyTableLeftWrapper>
        <VacancyTableRightWrapper>
          <DataTableRow header border="1px solid #DADCE0">
            {rateCalculationData.grossPotentialRent &&
              _.orderBy(rateCalculationData.grossPotentialRent.periods, p => moment(p.month), "asc").map(period => (
                <DataTableCell key={period.month} bold>
                  {moment.utc(period.month).format("M/D/YYYY")}
                </DataTableCell>
              ))}
          </DataTableRow>
          {rateCalculationData.suites &&
            rateCalculationData.suites.map(suite => {
              const { leases } = suite;

              return leases.map((lease, leaseIdx) => (
                <DataTableRow key={lease.id} border={leaseIdx < leases.length - 1 ? "1px dashed #DADCE0" : "1px solid #DADCE0"}>
                  {_.orderBy(lease.periods, p => moment(p.startDate), "asc").map(period => (
                    <StyledTooltip
                      content={selectedRentRollField.isStraightLine ? getStraightLineTooltipContent(period) : getTooltipContent(period)}
                      notToShow={period.isTheoretical}
                      children={
                        <DataTableCell key={period.startDate}>
                          {selectedRentRollField.isStraightLine ? getCellContentForLeaseInPeriodStraightLine(period) : getCellContentForLeaseInPeriod(period)}
                        </DataTableCell>
                      }
                    />
                  ))}
                </DataTableRow>
              ));
            })}
          <DataTableRow border="1px solid #DADCE0">
            {_.orderBy(rateCalculationData.grossPotentialRent.periods, p => moment(p.month), "asc").map(period => (
              <DataTableCell key={period.month}>{numeral(period.totalValue).format("$0,0.00")}</DataTableCell>
            ))}
          </DataTableRow>
        </VacancyTableRightWrapper>
      </VacancyTableWrapper>
    );
  };

  const getCellContentForLeaseInPeriod = period => {
    if (period.totalValue) {
      return numeral(period.totalValue).format("$0,0.00");
    }

    return "--";
  };

  const getCellContentForLeaseInPeriodStraightLine = period => {
    if (period.totalValue) {
      return numeral(period.totalValue).format("$0,0.00");
    }

    // BAR-3419: If cell total value is 0 and lease average rent value is 0 then return -- (no data).
    // Otherwise, return 0 because it means that (this cell rent value - average rent value) == 0
    return period.leaseAverageRentValue === 0 ? "--" : 0;
  };

  const getResultPreviewContent = () => {
    if (areAllLeasesVacant === true && marketRentType !== "manualEntry") {
      return null;
    } else if (calculationState.selectedAsOfDate && rateCalculationData) {
      return (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: "1em" }}>
            <SwitchWrapper>
              <StyledLockedSwitch value={isAnnualized} onChange={onAnnualizeSwitch} />
              <AnnualizedText>{`These calculations are${!isAnnualized ? " not" : ""} annualized.`}</AnnualizedText>
            </SwitchWrapper>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: "1em" }}>
            <ResultPreview>
              {Boolean(actualVacancyType) && (actualVacancyType === "economicPercentage" || actualVacancyType === "physicalPercentage") ? (
                <>
                  <LabelDecimal value={isAnnualized ? rateCalculationData.totalAnnualized * 100 : rateCalculationData.total * 100} decimals={3} />
                  %
                </>
              ) : (
                <LabelDollarFormatted value={isAnnualized ? rateCalculationData.totalAnnualized : rateCalculationData.total} decimals={2} />
              )}
            </ResultPreview>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: "1em" }}>
            <ResultPreviewLabel>RESULT PREVIEW</ResultPreviewLabel>
          </div>
        </>
      );
    }

    return null;
  };

  const getFinishButton = () => {
    if (areAllLeasesVacant === true && marketRentType !== "manualEntry") {
      return null;
    } else if (calculationState.selectedAsOfDate && rateCalculationData) {
      return (
        <PrimaryButton
          label={isSavingField ? "Saving..." : "Save"}
          style={{ marginLeft: "1em", backgroundColor: LB_BLUE }}
          size={ButtonSizeOptions.sm}
          onClick={onFinishClick}
          disabled={isCalculatedDataLoading}
        />
      );
    }

    return null;
  };

  const getCalculationBody = () => {
    const getTableOrText = () => {
      return areAllLeasesVacant === true && marketRentType !== "manualEntry" && rateCalculationData && !isCalculatedDataLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "2em", flexDirection: "column" }}>
          <InfoCircle>
            <img src={InfoIcon} alt="All suites are vacant during calculation period" />
          </InfoCircle>
          <InfoText>
            Because this property is completely vacant during the calculation period selected, you must click &quot;Back&quot; to enter a manual market rent on
            the prior screen.
          </InfoText>
        </div>
      ) : (
        getTable()
      );
    };

    return (
      <>
        {!calculationState.selectedAsOfDate ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "2em", flexDirection: "column" }}>
            <InfoCircle>
              <img src={InfoIcon} alt="No as of date selected" />
            </InfoCircle>
            <InfoText>Select a report group and as of date to view the vacancy rate calculation table</InfoText>
          </div>
        ) : (
          getTableOrText()
        )}
        {getResultPreviewContent()}
        <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: "1em", paddingTop: "0.5em" }}>
          <SecondaryButton label="Back" size={ButtonSizeOptions.sm} onClick={onBackClick} style={{ border: `1px solid ${LB_BLUE}`, color: LB_BLUE }} />
          {getFinishButton()}
        </div>
      </>
    );
  };

  return (
    <Screen2Wrapper maxW={drawerMaxWidth}>
      <StyledButtonText label={"Back"} iconName="arrow_forward" onClick={onBackClick} reverse />
      <VacancyHeaderRow pt={0} bb="none">
        <div style={{ fontWeight: "500", marginBottom: "0.5em", fontStyle: "normal" }}>
          {`Rent Roll Calculation for ${selectedProperty && selectedProperty.name}`}
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", width: "55%", paddingRight: "5px" }}>
            <p style={{ color: "rgba(0, 0, 0, 0.75)", fontSize: "12px", margin: "0", lineHeight: "normal" }}>
              Selecting an option from the Calculation Status dropdown will determine what value to use for the lease in the calculation.
            </p>
          </div>
          <div style={{ display: "flex", width: "45%" }}>
            <InputTypeAhead
              variant="filled"
              id="reportGroup"
              name="reportGroup"
              label="Report group"
              options={calculationState.reportGroupsOptions}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              onChange={onRGSelected}
              value={calculationState.selectedRGId}
              style={{ marginRight: "0.5em", width: "250px" }}
            />
            <InputTypeAhead
              variant="filled"
              id="asOfDate"
              name="asOfDate"
              label="As of date"
              options={calculationState.asOfDateOptions}
              displayWith={option => option && option.formattedDate}
              setValueWith={option => option && option.asOfDate}
              onChange={onAsOfDateSelected}
              value={calculationState.selectedAsOfDate}
              style={{ width: "200px" }}
              disabled={!reportConfigs || !calculationState.selectedRGId}
            />
          </div>
        </div>
      </VacancyHeaderRow>
      {getCalculationBody()}
    </Screen2Wrapper>
  );
});

export default VacancyRateCalculation;
